import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    TrendingUp, AlertTriangle, Sparkles, Target,
    Award, DollarSign, Gauge, Users, Clock, Bell, ArrowRight,
    Activity, BarChart3
} from 'lucide-react';
import AIWeeklySynthesisV2 from './AIWeeklySynthesisV2';

/**
 * DirigeantCockpit - Widget cockpit de pilotage pour le manager d'agence
 * 
 * Affiche 8 KPI strategiques en 30 secondes + synthese IA hebdo.
 * Se branche sur les donnees existantes (mandates, team, agencyStats) et
 * complete avec placeholders "—" pour les KPI pas encore disponibles.
 */
export function DirigeantCockpit({ mandates = [], team = [], stats = {}, showAIWeekly = true }) {

    // ═══════════════════════════════════════════════════════
    // COMPUTED KPIS
    // ═══════════════════════════════════════════════════════
    const kpis = useMemo(() => {
        const activeStatuses = ['draft', 'generated', 'sent', 'actif', 'active', 'acknowledged'];
        const activeMandates = mandates.filter(m => activeStatuses.includes(m.status));

        // CA encaisse mois (depuis agencyStats)
        const caEncaisse = stats?.total_revenue || 0;

        // Pipe pondere : somme (prix vente × proba) — par defaut 0.45 pour mandats actifs
        const pipePondere = activeMandates.reduce((acc, m) => {
            const price = m.pricing?.sale_price || 0;
            const fees = m.pricing?.agency_fees_pct ? (m.pricing.agency_fees_pct / 100) : 0.05;
            const proba = m.type === 'exclusive' ? 0.65 : 0.35;
            return acc + (price * fees * proba);
        }, 0);

        // Taux exclusivite
        const exclusiveCount = activeMandates.filter(m => m.type === 'exclusive' || m.type === 'exclusif').length;
        const tauxExclu = activeMandates.length > 0
            ? Math.round((exclusiveCount / activeMandates.length) * 100)
            : 0;

        // Age moyen portefeuille
        const now = new Date();
        const ages = activeMandates
            .map(m => {
                const created = m.created_at ? new Date(m.created_at) : null;
                if (!created) return null;
                return Math.floor((now - created) / (1000 * 60 * 60 * 24));
            })
            .filter(a => a !== null);
        const ageMoyen = ages.length > 0
            ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length)
            : 0;

        // Mandats sans suivi > 7j
        const mandatsSansSuivi = activeMandates.filter(m => {
            const lastAction = m.last_action_at || m.updated_at || m.created_at;
            if (!lastAction) return false;
            const diffDays = (now - new Date(lastAction)) / (1000 * 60 * 60 * 24);
            return diffDays > 7;
        }).length;

        // Top agent (premier membre actif si pas de perf data)
        const topAgent = team.find(t => t.role === 'agent' || t.role === 'agent_commercial') || team[0];

        // Mandats signes
        const mandatsSignes = stats?.signed_mandates || 0;

        return [
            {
                key: 'ca',
                label: 'CA encaissé',
                value: caEncaisse > 0 ? `${(caEncaisse / 1000).toFixed(1)}k€` : '0€',
                sub: 'Ce mois',
                icon: DollarSign,
                color: 'from-green-500/30 to-green-500/5',
                iconColor: 'text-green-400',
                borderColor: 'border-green-500/20',
                link: '/admin/agency',
            },
            {
                key: 'pipe',
                label: 'Pipeline pondéré',
                value: pipePondere > 0 ? `${(pipePondere / 1000).toFixed(0)}k€` : '—',
                sub: 'Honoraires × proba',
                icon: Gauge,
                color: 'from-[#681793]/30 to-[#681793]/5',
                iconColor: 'text-[#9b4dca]',
                borderColor: 'border-[#681793]/20',
                link: '/admin/mandates',
            },
            {
                key: 'exclu',
                label: 'Exclusivité',
                value: activeMandates.length > 0 ? `${tauxExclu}%` : '—',
                sub: `${exclusiveCount}/${activeMandates.length} mandats`,
                icon: Award,
                color: 'from-[#FF914D]/30 to-[#FF914D]/5',
                iconColor: 'text-[#FF914D]',
                borderColor: 'border-[#FF914D]/20',
                link: '/admin/mandates?type=exclusive',
            },
            {
                key: 'actifs',
                label: 'Mandats actifs',
                value: activeMandates.length,
                sub: 'En commercialisation',
                icon: BarChart3,
                color: 'from-blue-500/30 to-blue-500/5',
                iconColor: 'text-blue-400',
                borderColor: 'border-blue-500/20',
                link: '/admin/mandates',
            },
            {
                key: 'ventes',
                label: 'Ventes ce mois',
                value: stats?.closings_this_month || 0,
                sub: `${mandatsSignes} mandats signés`,
                icon: TrendingUp,
                color: 'from-emerald-500/30 to-emerald-500/5',
                iconColor: 'text-emerald-400',
                borderColor: 'border-emerald-500/20',
                link: '/admin/agency',
            },
            {
                key: 'age',
                label: 'Âge portefeuille',
                value: ageMoyen > 0 ? `${ageMoyen}j` : '—',
                sub: 'Moyenne mandats actifs',
                icon: Clock,
                color: 'from-amber-500/30 to-amber-500/5',
                iconColor: 'text-amber-400',
                borderColor: 'border-amber-500/20',
                link: '/admin/mandates',
            },
            {
                key: 'alertes',
                label: 'À surveiller',
                value: mandatsSansSuivi,
                sub: mandatsSansSuivi > 0 ? 'Mandats sans contact >7j' : 'Tout est à jour',
                icon: Bell,
                color: mandatsSansSuivi > 0 ? 'from-red-500/30 to-red-500/5' : 'from-slate-500/20 to-slate-500/5',
                iconColor: mandatsSansSuivi > 0 ? 'text-red-400' : 'text-slate-400',
                borderColor: mandatsSansSuivi > 0 ? 'border-red-500/20' : 'border-white/10',
                link: '/admin/mandates?alert=no_followup',
                alert: mandatsSansSuivi > 0,
            },
            {
                key: 'top',
                label: 'Top agent',
                value: topAgent ? `${topAgent.first_name?.[0] || ''}${topAgent.last_name?.[0] || ''}` : '—',
                sub: topAgent ? `${topAgent.first_name} ${topAgent.last_name || ''}`.trim() : 'Invitez votre équipe',
                icon: Users,
                color: 'from-[#681793]/30 to-[#FF914D]/20',
                iconColor: 'text-[#FF914D]',
                borderColor: 'border-[#681793]/20',
                link: '/admin/team',
            },
        ];
    }, [mandates, team, stats]);

    // ═══════════════════════════════════════════════════════
    // Current week label (used by header)
    // ═══════════════════════════════════════════════════════
    const currentWeek = useMemo(() => {
        const now = new Date();
        const weekNum = Math.ceil(((now - new Date(now.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
        const monthName = now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        return `Semaine ${weekNum} · ${monthName}`;
    }, []);

    // ═══════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-5"
            data-testid="dirigeant-cockpit"
        >
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                        <Gauge className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-bold">Cockpit dirigeant</p>
                        <p className="text-sm font-bold text-foreground">{currentWeek}</p>
                    </div>
                </div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/20 text-green-400 text-[10px] font-bold border border-green-500/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    Temps réel
                </span>
            </div>

            {/* 8 KPI tiles grid */}
            <div
                className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3"
                data-testid="dirigeant-kpi-grid"
            >
                {kpis.map((kpi, i) => (
                    <motion.div
                        key={kpi.key}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 + i * 0.04 }}
                    >
                        <Link to={kpi.link} data-testid={`cockpit-kpi-${kpi.key}`}>
                            <div className={`group relative p-3 sm:p-4 rounded-xl bg-gradient-to-br ${kpi.color} border ${kpi.borderColor} hover:border-white/30 transition-all h-full overflow-hidden`}>
                                {kpi.alert && (
                                    <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                                )}
                                <div className="flex items-center gap-2 mb-2">
                                    <kpi.icon className={`w-3.5 h-3.5 ${kpi.iconColor}`} />
                                    <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider truncate">{kpi.label}</p>
                                </div>
                                <p className="text-xl sm:text-2xl font-black text-foreground leading-none mb-1">{kpi.value}</p>
                                <p className="text-[10px] text-muted-foreground truncate">{kpi.sub}</p>
                                <ArrowRight className="absolute bottom-2 right-2 w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>

            {/* Synthese IA hebdo V2 — Control Room design (conditionnel : déplacée vers "Analyse approfondie" sur cockpit dirigeant) */}
            {showAIWeekly && <AIWeeklySynthesisV2 />}

            {/* Widgets repartis dans les autres onglets du dashboard parent (Priorites IA / Activite) */}
        </motion.div>
    );
}

export default DirigeantCockpit;
